import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import config from '../../config';
import { injectIntl, intlShape } from '../../util/reactIntl';
import routeConfiguration from '../../routeConfiguration';
import { createResourceLocatorString, pathByRouteName } from '../../util/routes';
import { logout } from '../../ducks/Auth.duck';
import DesktopMarketplaceHeader from '../DesktopMarketplaceHeader/DesktopMarketplaceHeader';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';

import css from './LandingHeader.module.css';

const SCROLLED_OPEN_THRESHOLD = 96;
const SCROLLED_CLOSE_THRESHOLD = 8;

const LandingHeaderComponent = props => {
  const {
    currentPage,
    currentSearchParams,
    currentUser,
    history,
    intl,
    isAuthenticated,
    currentUserHasListings,
    hideSearch,
    notificationCount,
    onLogout,
  } = props;
  const [isScrolled, setIsScrolled] = useState(false);
  const [isCompactSearchExpanded, setIsCompactSearchExpanded] = useState(false);
  const [searchActivationNonce, setSearchActivationNonce] = useState(0);
  const [layoutMetrics, setLayoutMetrics] = useState({
    backdropTop: 166,
    wizardTop: 182,
  });
  const headerRef = useRef(null);
  const searchAnchorRef = useRef(null);
  const layoutFrameRef = useRef(null);
  const layoutTimeoutRef = useRef(null);

  const measureLayout = useCallback(() => {
    const headerBottom = headerRef.current
      ? Math.round(headerRef.current.getBoundingClientRect().bottom)
      : 166;
    const searchBottom = searchAnchorRef.current
      ? Math.round(searchAnchorRef.current.getBoundingClientRect().bottom)
      : headerBottom;

    setLayoutMetrics(prevState => {
      if (
        prevState.backdropTop === headerBottom &&
        prevState.wizardTop === searchBottom + 14
      ) {
        return prevState;
      }

      return {
        backdropTop: headerBottom,
        wizardTop: searchBottom + 14,
      };
    });
  }, []);

  const cancelScheduledLayoutMeasurement = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (layoutFrameRef.current) {
      window.cancelAnimationFrame(layoutFrameRef.current);
      layoutFrameRef.current = null;
    }

    if (layoutTimeoutRef.current) {
      window.clearTimeout(layoutTimeoutRef.current);
      layoutTimeoutRef.current = null;
    }
  }, []);

  const scheduleLayoutMeasurement = useCallback(() => {
    if (typeof window === 'undefined') {
      measureLayout();
      return;
    }

    cancelScheduledLayoutMeasurement();

    let remainingFrames = 2;
    const runMeasurement = () => {
      measureLayout();
      remainingFrames -= 1;

      if (remainingFrames > 0) {
        layoutFrameRef.current = window.requestAnimationFrame(runMeasurement);
      } else {
        layoutFrameRef.current = null;
      }
    };

    layoutFrameRef.current = window.requestAnimationFrame(runMeasurement);
    layoutTimeoutRef.current = window.setTimeout(() => {
      measureLayout();
      layoutTimeoutRef.current = null;
    }, 260);
  }, [cancelScheduledLayoutMeasurement, measureLayout]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleScroll = () => {
      const nextScrollY = window.scrollY || 0;
      setIsScrolled(prevState =>
        prevState
          ? nextScrollY > SCROLLED_CLOSE_THRESHOLD
          : nextScrollY > SCROLLED_OPEN_THRESHOLD
      );
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isScrolled || !isCompactSearchExpanded) {
      return;
    }

    setIsCompactSearchExpanded(false);
  }, [isScrolled, isCompactSearchExpanded]);

  useEffect(() => {
    scheduleLayoutMeasurement();
  }, [isScrolled, isCompactSearchExpanded, scheduleLayoutMeasurement]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleResize = () => {
      scheduleLayoutMeasurement();
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelScheduledLayoutMeasurement();
    };
  }, [cancelScheduledLayoutMeasurement, scheduleLayoutMeasurement]);

  const handleApplySearchParams = searchParams => {
    history.push(createResourceLocatorString('SearchPage', routeConfiguration(), {}, searchParams));
  };

  const handleLogout = useCallback(() => {
    onLogout().then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration());

      // In production we ensure that data is really lost,
      // but in development mode we use stored values for debugging
      if (config.dev) {
        history.push(path);
      } else if (typeof window !== 'undefined') {
        window.location = path;
      }
    });
  }, [history, onLogout]);

  const handleCompactSearchClick = () => {
    setIsCompactSearchExpanded(true);
    setSearchActivationNonce(prevState => prevState + 1);
  };

  return (
    <div
      className={css.root}
      style={{
        '--landingBackdropTop': `${layoutMetrics.backdropTop}px`,
        '--landingWizardTop': `${layoutMetrics.wizardTop}px`,
      }}
    >
      <div className={css.mobileOnly}>
        <TopbarContainer currentPage={currentPage} currentSearchParams={currentSearchParams} />
      </div>

      <div className={css.desktopOnly}>
        <DesktopMarketplaceHeader
          compact={isScrolled}
          currentPage={currentPage}
          currentSearchParams={currentSearchParams}
          currentUser={currentUser}
          elevated={isScrolled}
          headerRef={headerRef}
          intl={intl}
          isAuthenticated={isAuthenticated}
          currentUserHasListings={currentUserHasListings}
          notificationCount={notificationCount}
          onLogout={isAuthenticated ? handleLogout : null}
          onApplySearchParams={handleApplySearchParams}
          onCompactSearchClose={() => {
            if (isScrolled) {
              setIsCompactSearchExpanded(false);
            }
          }}
          onCompactSearchOpen={handleCompactSearchClick}
          onSearchLayoutChange={scheduleLayoutMeasurement}
          searchActivationNonce={searchActivationNonce}
          searchAnchorRef={searchAnchorRef}
          showCompactSearchExpanded={isCompactSearchExpanded}
          showEmail={!isScrolled && !hideSearch}
          showExpandedSearchRow={!hideSearch && (!isScrolled || isCompactSearchExpanded)}
          showSearch={!hideSearch}
        />
      </div>
    </div>
  );
};

LandingHeaderComponent.defaultProps = {
  currentPage: null,
  currentSearchParams: null,
  currentUser: null,
  hideSearch: false,
};

const { bool, func, number, object, string } = PropTypes;

LandingHeaderComponent.propTypes = {
  currentPage: string,
  currentSearchParams: object,
  currentUser: object,
  history: object.isRequired,
  intl: intlShape.isRequired,
  isAuthenticated: bool.isRequired,
  currentUserHasListings: bool.isRequired,
  // The home hides the header search while its own search in the hero is on screen.
  hideSearch: bool,
  notificationCount: number.isRequired,
  onLogout: func.isRequired,
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.Auth;
  const { currentUser, currentUserHasListings, currentUserNotificationCount } = state.user;

  return {
    isAuthenticated,
    currentUser,
    currentUserHasListings,
    notificationCount: currentUserNotificationCount || 0,
  };
};

const mapDispatchToProps = dispatch => ({
  onLogout: historyPush => dispatch(logout(historyPush)),
});

const LandingHeader = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(LandingHeaderComponent);

export default LandingHeader;
